import { PrimaryButton } from "@/components/buttons/PrimaryButton";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "@/utils/toast";
import { useForm } from "@tanstack/react-form";
import { type OTPVerificationForm } from "@/forms/auth/auth.form";
import { PencomRoutes } from "@/router/PencomRoutes";

import { useOTPRateLimiter } from "@/hooks/useOTPRateLimiter";
import PencomOTPField from "@/components/input/PencomOTPField";
import type { RegisterEmployerFormDto } from "@/models/dto/register-employer.dto";
import {
  useResendEmployerOTP,
  useRegisterEmployer,
} from "@/hooks/employer-registration/employer-registration.hook";
import { EmployerRegStepIndicator } from "@/components/onboarding/EmployerRegStepIndicator";

const EmployerRegEmailVerificationPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const data = location.state as {
    contactEmail?: string;
    formData?: RegisterEmployerFormDto;
  };

  const registerEmployerMutation = useRegisterEmployer();
  const resendEmployerOTPMutation = useResendEmployerOTP();
  const { canResend, timeRemaining, startCooldown } = useOTPRateLimiter(60);

  const contactEmail = data?.contactEmail || "";
  const formData = data?.formData;

  const form = useForm({
    defaultValues: {
      otp: "",
    } as OTPVerificationForm,
    onSubmit: async ({ value }) => {
      if (!formData) {
        toast.error("Form data missing. Please go back and fill the form again.");
        return;
      }

      registerEmployerMutation.mutate(
        {
          ...formData,
          otp: value.otp,
        },
        {
          onSuccess: () => {
            toast.success("Registration successful!");
            navigate(PencomRoutes.EMPLOYER_REG_SUCCESS, { state: data });
          },
          onError: (error) => {
            form.setFieldValue("otp", "");
            toast.error(error.message || "Invalid or expired code. Please try again.");
          },
        },
      );
    },
  });

  const handleResendOTP = () => {
    if (!canResend || resendEmployerOTPMutation.isPending) return;
    if (!formData?.companyName) {
      toast.error("Session expired. Please go back and fill the form again.");
      return;
    }

    resendEmployerOTPMutation.mutate(
      {
        companyName: formData.companyName,
        email: contactEmail.trim(),
      },
      {
        onSuccess: () => {
          toast.success("OTP resent successfully");
          startCooldown();
        },
        onError: (error) => {
          toast.error(error.message || "Failed to resend OTP");
        },
      },
    );
  };

  return (
    <div className="bg-off-white min-h-screen w-full flex flex-col px-8 lg:px-[100px]">
      <div className="w-full flex flex-col max-w-[460px] mx-auto mt-10">
        <EmployerRegStepIndicator />
        <div className="flex flex-col gap-y-8 items-start text-center w-full mt-6">
          <div className="flex flex-col gap-y-4 w-full">
            <h2 className="text-2xl font-semibold font-clash-display text-black-dark-grey tracking-[-0.36px] leading-tight w-full">
              We've sent a code to your email
            </h2>
            <p className="text-medium-dark-grey text-base font-regular font-clash-display w-full tracking-[-0.08px] leading-[18px]">
              To continue, enter the 6 digit code sent to{" "}
              <span className="font-semibold">{contactEmail}</span>
            </p>
          </div>

          <div className="flex flex-col gap-y-10 w-full">
            <div className="flex flex-col gap-y-2 w-full">
              <form.Field
                name="otp"
                validators={{
                  onChange: ({ value }) => {
                    if (!value) return undefined;
                    if (value.length !== 6) return "OTP must be 6 digits";
                    return undefined;
                  },
                }}
                children={(field) => (
                  <PencomOTPField
                    id={field.name}
                    label=""
                    length={6}
                    value={field.state.value}
                    onChange={(e) => {
                      if (registerEmployerMutation.error) registerEmployerMutation.reset();
                      field.handleChange(e.target.value);
                    }}
                    masked
                    bg="bg-white"
                    maskChar="•"
                    maskDelay={1500}
                    error={
                      field.state.meta.errors.length > 0 ||
                      !!registerEmployerMutation.error
                    }
                    helperText={
                      field.state.meta.errors.length > 0
                        ? (field.state.meta.errors[0] as string)
                        : registerEmployerMutation.error
                          ? "Invalid or expired code. Please try again."
                          : undefined
                    }
                  />
                )}
              />
            </div>

            <form.Subscribe
              selector={(state) => [state.values.otp]}
              children={([otp]) => (
                <PrimaryButton
                  title={
                    registerEmployerMutation.isPending
                      ? "Verifying..."
                      : "Verify Code"
                  }
                  onClick={form.handleSubmit}
                  disabled={
                    otp.length !== 6 || registerEmployerMutation.isPending
                  }
                  isLoading={registerEmployerMutation.isPending}
                />
              )}
            />
          </div>
        </div>

        <div className="bg-white border border-stroke rounded-lg p-4 flex flex-col gap-y-4 mt-6">
          <div className="flex items-center gap-x-2">
            <img
              src="/assets/icons/info-outline.svg"
              alt="Info"
              className="w-4 h-4 shrink-0"
            />
            <p className="text-sm font-medium font-clash-display text-medium-dark-grey tracking-[-0.07px]">
              Didn't get code?
            </p>
          </div>
          <p className="text-sm font-regular font-clash-display text-medium-grey leading-[18px] tracking-[-0.07px]">
            Please check your SPAM or JUNK folder if you don't see it. If
            it&apos;s still not there, click the button below and a new one
            will be sent to you.
          </p>
          <button
            type="button"
            onClick={handleResendOTP}
            disabled={!canResend || resendEmployerOTPMutation.isPending}
            className="bg-lightest-green text-primary-button font-clash-display font-semibold text-xs h-[30px] px-4 rounded-[3px] w-fit transition-all tracking-[-0.06px] disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-80 cursor-pointer"
          >
            {resendEmployerOTPMutation.isPending
              ? "Sending..."
              : !canResend
                ? `Resend otp in ${timeRemaining}s`
                : "Resend email"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EmployerRegEmailVerificationPage;
